import { __extends } from "tslib";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { ListenableEvent } from "../../utils";
import { clone } from "../../utils/array";
import { EPSILON, eq } from "../../utils/math";
import { BoundingBox } from "../mdl/BoundingBox";
import { SceneElement } from "../mdl/SceneElement";
import { removeFromScene, setLocalMatrix } from "../utils/MeshUtils";
import { ThickLinesMesh } from "../utils/ThickLinesMesh";
import { Camera } from "../view/Camera";
import { CameraMode } from "../view/CameraControl";
import { PointerInfo } from "../view/InputState";
import { CoordinatesType, ViewLayerMaskFlags } from "../view/View";
import { Viewer } from "../view/Viewer";
import { AbstractTool } from "./AbstractTool";
import { RectangleSelect } from "./RectangleSelect";
import { InteractionMode } from "./Tool";
var SelectionInfo = /** @class */ (function () {
    function SelectionInfo() {
        this.bounds = new BoundingBox();
        this.worldMatrix = Matrix.Identity();
        this.canRotate = true;
        this.canTranslate = true;
        this.canDelete = true;
        this.update();
    }
    SelectionInfo.prototype.update = function () {
        // prevent numerical issues with flat objects
        var tSize = this.bounds.getSize();
        if (eq(tSize.x, 0)) {
            this.bounds.min.x -= EPSILON;
            this.bounds.max.x += EPSILON;
        }
        if (eq(tSize.y, 0)) {
            this.bounds.min.y -= EPSILON;
            this.bounds.max.y += EPSILON;
        }
        if (eq(tSize.z, 0)) {
            this.bounds.min.z -= EPSILON;
            this.bounds.max.z += EPSILON;
        }
        this.worldBounds = this.bounds.transform(this.worldMatrix);
        this.worldPoints = this.bounds.getPoints(this.worldMatrix);
    };
    return SelectionInfo;
}());
export { SelectionInfo };
var SelectionDefault = /** @class */ (function (_super) {
    __extends(SelectionDefault, _super);
    function SelectionDefault(pApp) {
        var _this = _super.call(this, pApp) || this;
        _this.eventRectangleSelectFinished = new ListenableEvent(_this);
        /**
         * Will be triggered (after the double click delay) if the user clicks/tabs
         * on the background and there is currently no selection.
         */
        _this.eventBackgroundClick = new ListenableEvent(_this);
        /** Will be triggered immediately if the user clicks/tabs on the background and there is currently no selection. */
        _this.eventBackgroundClickImmediate = new ListenableEvent(_this);
        /** Will be triggered if the user clicks/tabs on a SceneElement. */
        _this.eventElementClick = new ListenableEvent(_this);
        /** Will be triggered if the state of the multi selection mode has changed. */
        _this.eventMultiSelectModeChanged = new ListenableEvent(_this);
        _this.onSelectionChanged = function (pParameter) {
            _this.mSelectionChanged = true;
            //stop multi selection mode if selection is empty
            if (_this.mMultiSelectMode && _this.multiSelectAutoStartEnabled && _this.mApp.model.selection.length === 0) {
                _this.setMultiSelectMode(false);
            }
            _this.mApp.viewer.requestRenderFrame();
        };
        _this.onCameraChanged = function (pParameter) {
            _this.mCameraChanged = true;
            _this.mApp.viewer.requestRenderFrame();
        };
        _this.onCameraModeChanged = function (pParameter) {
            _this.mCameraModeChanged = true;
            _this.mApp.viewer.requestRenderFrame();
        };
        _this.onBeforeRender = function (pParameter) {
            var tUpdateInteractors = false;
            if (_this.mSelectionChanged) {
                tUpdateInteractors = true;
                _this.mSelectionChanged = false;
                _this.updateSelection();
            }
            if (_this.mCameraModeChanged) {
                tUpdateInteractors = true;
                _this.mCameraModeChanged = false;
            }
            if (tUpdateInteractors || _this.mCameraChanged) {
                _this.updateInteractors(!tUpdateInteractors);
                _this.mCameraChanged = false;
            }
        };
        _this.onBeforeRenderCamera = function (pCamera) {
            if (pCamera instanceof Camera) {
                _this.updateSelectionBoxSides(pCamera.view);
            }
        };
        _this.onTriggerBackgroundClick = function () {
            _this.eventBackgroundClick.trigger();
            _this.mBackgroundClickTimer = null;
        };
        _this.onHandleLongTouch = function () {
            _this.mLongTouchTimer = null;
            //multi selection
            if (_this.mPickedElement != null) {
                if (_this.handleSelection(_this.mPickedElement, true)) {
                    _this.mPickedElement = null;
                    _this.mHandleSelection = false;
                    _this.mHandleClick = false;
                }
            }
        };
        _this.multiSelectAutoStartEnabled = false;
        _this.multiSelectDeselectAllEnabled = true;
        _this.subElementSelectionEnabled = true;
        _this.deselectionEnabled = true;
        _this.leftMouseButtonRectangleSelectEnabled = false;
        _this.rectangleSelectMode = "Complete";
        _this.mSelectionBoxSides = new Array();
        _this.mSelectionBoxSideGeom = new Array();
        _this.mPickedElement = null;
        _this.mSelection = new Array();
        _this.mInteractorElements = new Array();
        _this.mView = _this.mApp.viewer.view;
        _this.mPickedPos = Vector3.Zero();
        _this.mSelectionInfo = new SelectionInfo();
        _this.mRectSelectTool = null;
        _this.mSelectionChanged = false;
        _this.mCameraChanged = false;
        _this.mCameraModeChanged = false;
        _this.mHandleSelection = false;
        _this.mHandleClick = false;
        _this.mLongTouchTimer = null;
        _this.mMultiSelectMode = false;
        _this.mShowMainElementSelection = true;
        _this.mShowSubElementSelection = true;
        _this.mShowElementInteractors = true;
        _this.mBackgroundClickTimer = null;
        _this.prepareSelectionBoxSides();
        return _this;
    }
    Object.defineProperty(SelectionDefault.prototype, "multiSelectMode", {
        get: function () {
            return (this.mMultiSelectMode);
        },
        set: function (pEnabled) {
            this.setMultiSelectMode(pEnabled);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SelectionDefault.prototype, "showMainElementSelection", {
        get: function () {
            return (this.mShowMainElementSelection);
        },
        set: function (pShow) {
            if (this.mShowMainElementSelection !== pShow) {
                this.mShowMainElementSelection = pShow;
                this.updateSelectionIfRunning();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SelectionDefault.prototype, "showSubElementSelection", {
        get: function () {
            return (this.mShowSubElementSelection);
        },
        set: function (pShow) {
            if (this.mShowSubElementSelection !== pShow) {
                this.mShowSubElementSelection = pShow;
                this.updateSelectionIfRunning();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SelectionDefault.prototype, "showElementInteractors", {
        get: function () {
            return (this.mShowElementInteractors);
        },
        set: function (pShow) {
            if (this.mShowElementInteractors !== pShow) {
                this.mShowElementInteractors = pShow;
                this.updateSelectionIfRunning();
            }
        },
        enumerable: false,
        configurable: true
    });
    SelectionDefault.prototype.getInteractionMode = function () {
        if (this.mRectSelectTool != null)
            return (this.mRectSelectTool.getInteractionMode());
        else
            return (_super.prototype.getInteractionMode.call(this));
    };
    SelectionDefault.prototype.start = function (pParameter) {
        this.mApp.model.eventSelectionChanged.addListener(this.onSelectionChanged);
        this.mApp.viewer.eventBeforeRender.addListener(this.onBeforeRender);
        this.mApp.model.scene.onBeforeCameraRenderObservable.add(this.onBeforeRenderCamera);
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        this.mView.cameraControl.eventCameraModeChanged.addListener(this.onCameraModeChanged);
        this.mPickedElement = null;
        this.mSelectionChanged = true;
        this.mCameraChanged = false;
        this.mCameraModeChanged = false;
        this.mHandleSelection = false;
        this.mHandleClick = false;
        this.mBackgroundClickTimer = null;
        this.mLongTouchTimer = null;
        this.mApp.viewer.requestRenderFrame();
        //stop multi selection mode if selection is empty
        if (this.mMultiSelectMode && this.multiSelectAutoStartEnabled && this.mApp.model.selection.length === 0) {
            this.setMultiSelectMode(false);
        }
        return (_super.prototype.start.call(this, pParameter));
    };
    SelectionDefault.prototype.stop = function () {
        //rectangle select
        if (this.mRectSelectTool != null)
            this.mRectSelectTool.stop();
        this.mApp.model.eventSelectionChanged.removeListener(this.onSelectionChanged);
        this.mApp.viewer.eventBeforeRender.removeListener(this.onBeforeRender);
        this.mApp.model.scene.onBeforeCameraRenderObservable.removeCallback(this.onBeforeRenderCamera);
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        this.mView.cameraControl.eventCameraModeChanged.removeListener(this.onCameraModeChanged);
        this.clearSelection();
        this.resetBackgroundClickTimer();
        this.mApp.viewer.requestRenderFrame();
        _super.prototype.stop.call(this);
    };
    SelectionDefault.prototype.handlePointerDown = function (pEvent, pState) {
        //rectangle select
        if (this.mRectSelectTool != null) {
            this.mRectSelectTool.handlePointerDown(pEvent, pState);
            return;
        }
        this.mHandleClick = true;
        this.mHandleSelection = false;
        var tIsPrimaryPointer = pState.isPrimaryPointer(pEvent);
        var tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        if (tIsPrimaryPointer) {
            //pick interactors
            if (this.pickInteractors(tPointer))
                return;
            //start long touch
            this.resetLongTouchTimer();
            this.mLongTouchTimer = window.setTimeout(this.onHandleLongTouch, SelectionDefault.LONG_TOUCH_TIMEOUT);
        }
        //pick model
        if (this.mPickedElement == null) {
            this.mPickedElement = this.getPickedElement(tPointer.position, !tPointer.isMousePointer, this.mPickedPos);
        }
        //disable camera control and enable selection handling
        if (this.mPickedElement != null || this.mSelection.length > 0) {
            this.setInteractionMode(InteractionMode.Primary);
            this.mHandleSelection = true;
        }
    };
    SelectionDefault.prototype.handlePointerMove = function (pEvent, pState) {
        //rectangle select
        if (this.mRectSelectTool != null) {
            this.mRectSelectTool.handlePointerMove(pEvent, pState);
            return;
        }
        if (pState.isMouseHoverPointer(pEvent)) {
            this.updateInteractorHover(new Vector2(pEvent.clientX, pEvent.clientY));
            return;
        }
        //pointer moved
        var tPointer = pState.getPointer(pEvent.pointerId);
        if (this.pointerWasMoved(tPointer)) {
            //stop long touch
            this.resetLongTouchTimer();
            //start rectangle selection with left mouse button
            if (this.leftMouseButtonRectangleSelectEnabled &&
                (this.mPickedElement == null || !this.mPickedElement.getTopLevelElement().isSelected) &&
                pState.isPrimaryPointer(pEvent) && PointerInfo.IsMousePointer(pEvent)) {
                this.startPointerDownRectangleSelect();
                return;
            }
            //reenable camera control and disable click handling
            if (this.mHandleClick) {
                this.setInteractionMode(InteractionMode.None);
                this.mHandleClick = false;
            }
            this.startPointerMoveTool(pEvent, pState);
        }
    };
    SelectionDefault.prototype.handlePointerUp = function (pEvent, pState) {
        //rectangle select
        if (this.mRectSelectTool != null) {
            this.mRectSelectTool.handlePointerUp(pEvent, pState);
            return;
        }
        var tPickedElement = this.mPickedElement;
        this.resetPickingStates();
        var tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null || !pState.isPrimaryPointer(pEvent))
            return;
        //pointer was not moved and no long touch ==> handle click/tap
        if (!this.mHandleClick)
            return;
        var tBackgroundClick = true;
        //selection handling
        tBackgroundClick = !this.handleSelection(tPickedElement, pEvent.ctrlKey);
        //trigger background click event
        if (tBackgroundClick) {
            this.eventBackgroundClickImmediate.trigger();
            //trigger delayed background click event (to ignore double clicks)
            if (this.mBackgroundClickTimer == null) {
                this.mBackgroundClickTimer = window.setTimeout(this.onTriggerBackgroundClick, Viewer.MAX_DOUBLE_CLICK_DELAY * 1.2); //360ms
            }
            else {
                this.resetBackgroundClickTimer();
            }
        }
    };
    SelectionDefault.prototype.startRectangleSelect = function () {
        var _this = this;
        this.mRectSelectTool = new RectangleSelect(this.mApp);
        this.mRectSelectTool.multiSelectMode = this.mMultiSelectMode;
        this.mRectSelectTool.mode = this.rectangleSelectMode;
        this.mRectSelectTool.eventStopped.addListener(function () {
            _this.mRectSelectTool = null;
            _this.eventRectangleSelectFinished.trigger();
        });
    };
    SelectionDefault.prototype.updateInteractors = function (pOnlyCameraChanged) { };
    SelectionDefault.prototype.updateInteractorHover = function (pPos) { };
    SelectionDefault.prototype.pickInteractors = function (pPointer) { return (false); };
    SelectionDefault.prototype.startPointerMoveTool = function (pEvent, pState) { };
    SelectionDefault.prototype.getDirectionToCamera = function (pPos, pView) {
        var tCameraVec;
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            tCameraVec = pView.cameraControl.position.subtract(pPos);
            tCameraVec.normalize();
        }
        else
            tCameraVec = pView.cameraControl.direction.negate();
        return (tCameraVec);
    };
    SelectionDefault.prototype.resetBackgroundClickTimer = function () {
        if (this.mBackgroundClickTimer != null) {
            clearTimeout(this.mBackgroundClickTimer);
            this.mBackgroundClickTimer = null;
        }
    };
    SelectionDefault.prototype.resetLongTouchTimer = function () {
        if (this.mLongTouchTimer != null) {
            clearTimeout(this.mLongTouchTimer);
            this.mLongTouchTimer = null;
        }
    };
    SelectionDefault.prototype.pointerWasMoved = function (pPointer) {
        return (pPointer != null &&
            (Math.abs(pPointer.initialPosition.x - pPointer.position.x) > Viewer.POINTER_MOVE_TOLERANCE ||
                Math.abs(pPointer.initialPosition.y - pPointer.position.y) > Viewer.POINTER_MOVE_TOLERANCE));
    };
    SelectionDefault.prototype.setMultiSelectMode = function (pEnabled) {
        if (this.mMultiSelectMode !== pEnabled) {
            this.mMultiSelectMode = pEnabled;
            if (this.mRectSelectTool != null)
                this.mRectSelectTool.multiSelectMode = this.mMultiSelectMode;
            if (!this.mMultiSelectMode && this.mApp.model.selection.length > 1)
                this.mApp.model.setSelection(null);
            this.eventMultiSelectModeChanged.trigger();
        }
    };
    SelectionDefault.prototype.handleSelection = function (pPickedElement, pMultiSelect) {
        if (!this.mHandleSelection)
            return (false);
        var tHandled = false;
        this.mApp.model.beginSelectionChange();
        if (pPickedElement != null) {
            //trigger click event
            this.eventElementClick.trigger(pPickedElement);
            var tTopLevelElement = pPickedElement.getTopLevelElement();
            var tMainElement = pPickedElement.getMainElement();
            var tSubElement = pPickedElement.getSelectableElement();
            if (!this.subElementSelectionEnabled || tMainElement !== tTopLevelElement || tMainElement === tSubElement)
                tSubElement = null;
            if (pMultiSelect || this.mMultiSelectMode) {
                //multi selection
                //start multiSelection mode (if not already started)
                var tMultiSelectStarted = false;
                if (this.multiSelectAutoStartEnabled && !this.mMultiSelectMode) {
                    this.setMultiSelectMode(true);
                    tMultiSelectStarted = true;
                }
                if (tTopLevelElement.isSelected) {
                    //remove from selection
                    if (this.deselectionEnabled || this.mApp.model.selection.length > 1) {
                        if (!tMultiSelectStarted) {
                            this.mApp.model.removeFromSelection(tTopLevelElement);
                        }
                        tHandled = true;
                    }
                }
                else {
                    //add to selection
                    this.mApp.model.addToSelection(tTopLevelElement);
                    this.mApp.model.setSubElementSelection(tMainElement, tSubElement);
                    tHandled = true;
                }
            }
            else {
                //single selection
                if (!tTopLevelElement.isSelected || this.mApp.model.selection.length > 1)
                    this.mApp.model.setSelection([tTopLevelElement]);
                this.mApp.model.setSubElementSelection(tMainElement, tSubElement);
                tHandled = true;
            }
        }
        else {
            //deselect
            if (!pMultiSelect && (!this.mMultiSelectMode || this.multiSelectDeselectAllEnabled)) {
                if (this.deselectionEnabled) {
                    this.mApp.model.setSelection(null);
                    tHandled = true;
                }
                else {
                    if (this.mApp.model.hasSubElementSelection()) {
                        this.mApp.model.clearSubElementSelection();
                        tHandled = true;
                    }
                }
            }
        }
        this.mApp.model.endSelectionChange();
        return (tHandled);
    };
    SelectionDefault.prototype.getPickedElement = function (pPos, pUseFrustum, rPickPos) {
        var tPickInfo = null;
        if (pUseFrustum)
            tPickInfo = this.mView.pickSceneWithFrustum(pPos, SelectionDefault.PICK_FRUSTUM_SIZE, SelectionDefault.PICK_FRUSTUM_SIZE, CoordinatesType.Client);
        else
            tPickInfo = this.mView.pickScene(pPos, CoordinatesType.Client);
        if (tPickInfo != null && tPickInfo.hit && tPickInfo.pickedMesh != null) {
            if (rPickPos != null && tPickInfo.pickedPoint != null)
                rPickPos.copyFrom(tPickInfo.pickedPoint);
            return (SceneElement.FromNode(tPickInfo.pickedMesh, true));
        }
        return (null);
    };
    SelectionDefault.prototype.clearSelection = function () {
        for (var _i = 0, _a = this.mSelectionBoxSides; _i < _a.length; _i++) {
            var tBox = _a[_i];
            tBox.dispose();
        }
        this.mSelectionBoxSides = new Array();
        for (var _b = 0, _c = this.mSelection; _b < _c.length; _b++) {
            var tElement = _c[_b];
            //unregister events
            tElement.eventBoundingBoxChanged.removeListener(this.onSelectionChanged);
            tElement.eventTransformationChanged.removeListener(this.onSelectionChanged);
        }
        this.mSelection = new Array();
        //hide interactors
        for (var _d = 0, _e = this.mInteractorElements; _d < _e.length; _d++) {
            var tElement = _e[_d];
            tElement.showInteractors(false);
        }
        this.mInteractorElements = new Array();
    };
    SelectionDefault.prototype.updateSelectionIfRunning = function () {
        if (this.isRunning()) {
            this.updateSelection();
            this.mApp.viewer.requestRenderFrame();
        }
    };
    SelectionDefault.prototype.updateSelection = function () {
        this.clearSelection();
        this.mSelection = clone(this.mApp.model.selection);
        this.mSelectionInfo.bounds.reset();
        Matrix.IdentityToRef(this.mSelectionInfo.worldMatrix);
        this.mSelectionInfo.canDelete = true;
        this.mSelectionInfo.canRotate = true;
        this.mSelectionInfo.canTranslate = true;
        for (var _i = 0, _a = this.mSelection; _i < _a.length; _i++) {
            var tElement = _a[_i];
            //register events
            tElement.eventBoundingBoxChanged.addListener(this.onSelectionChanged);
            tElement.eventTransformationChanged.addListener(this.onSelectionChanged);
            var tBBox = tElement.getSelectionHighlightBoundingBox().clone();
            var tTransform = tElement.worldTransform;
            if (this.mShowMainElementSelection && tElement.isSelectionHighlightEnabled()) {
                this.createSelectionBox(tBBox, tTransform, false);
            }
            if (this.mSelection.length === 1) {
                this.mSelectionInfo.bounds = tBBox;
                this.mSelectionInfo.worldMatrix = tTransform.clone();
            }
            else {
                tBBox.transformInPlace(tTransform);
                this.mSelectionInfo.bounds.expandInPlace(tBBox);
            }
            this.mSelectionInfo.canDelete = this.mSelectionInfo.canDelete && tElement.canDelete();
            this.mSelectionInfo.canRotate = this.mSelectionInfo.canRotate && tElement.canRotate();
            this.mSelectionInfo.canTranslate = this.mSelectionInfo.canTranslate && tElement.canTranslate();
            //sub selection
            var tSubElement = this.mApp.model.getSubElementSelection(tElement);
            if (tSubElement != null) {
                if (this.mShowSubElementSelection && tSubElement.isSelectionHighlightEnabled()) {
                    this.createSelectionBox(tSubElement.boundingBoxVisible, tSubElement.worldTransform, true);
                }
                //show interactors
                if (this.mShowElementInteractors) {
                    tSubElement.showInteractors(true);
                    this.mInteractorElements.push(tSubElement);
                }
            }
            else {
                //show interactors
                if (this.mShowElementInteractors) {
                    tElement.showInteractors(true);
                    this.mInteractorElements.push(tElement);
                }
            }
        }
        this.mSelectionInfo.update();
    };
    SelectionDefault.prototype.prepareSelectionBoxSides = function () {
        var tPoints = new BoundingBox(new Vector3(-0.5, -0.5, -0.5), new Vector3(0.5, 0.5, 0.5)).getPoints();
        this.mSelectionBoxSideGeom = new Array();
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MIN_X);
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MAX_X);
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MIN_Y);
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MAX_Y);
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MIN_Z);
        this.prepareSelectionBoxSide(tPoints, BoundingBox.SIDE_MAX_Z);
    };
    SelectionDefault.prototype.prepareSelectionBoxSide = function (pBBPoints, pIndices) {
        var tLines = new Array();
        tLines.push(pBBPoints[pIndices[0]]);
        tLines.push(pBBPoints[pIndices[1]]);
        tLines.push(pBBPoints[pIndices[1]]);
        tLines.push(pBBPoints[pIndices[2]]);
        tLines.push(pBBPoints[pIndices[2]]);
        tLines.push(pBBPoints[pIndices[3]]);
        tLines.push(pBBPoints[pIndices[3]]);
        tLines.push(pBBPoints[pIndices[0]]);
        var tV1 = pBBPoints[pIndices[1]].subtract(pBBPoints[pIndices[0]]);
        var tV2 = pBBPoints[pIndices[2]].subtract(pBBPoints[pIndices[0]]);
        var tNormal = Vector3.Cross(tV1, tV2).normalize();
        var tCenter = Vector3.Center(pBBPoints[pIndices[0]], pBBPoints[pIndices[2]]);
        var tSide = ThickLinesMesh.CreateThickLines("selectionBoxSideGeom", this.mApp.model.scene, tLines);
        tSide.isPickable = false;
        tSide.thickness = 2.0;
        tSide.renderingGroupId = 1;
        tSide.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        tSide.boxSide_normal = tNormal;
        tSide.boxSide_center = tCenter;
        removeFromScene(tSide, this.mApp.model.scene);
        this.mSelectionBoxSideGeom.push(tSide);
    };
    SelectionDefault.prototype.createSelectionBox = function (pBoundingBox, pTransform, pSubSelection) {
        if (pBoundingBox == null || pTransform == null)
            return;
        var tCenter = pBoundingBox.getCenter();
        var tColor = pSubSelection ? Color4.FromHexString("#868686FF") :
            Color4.FromHexString(this.mApp.theme.mainColorBright + "FF");
        //build pivot matrix
        var tMPivot = Matrix.Scaling((pBoundingBox.max.x - pBoundingBox.min.x) + SelectionDefault.SELECTION_BOX_OFFSET, (pBoundingBox.max.y - pBoundingBox.min.y) + SelectionDefault.SELECTION_BOX_OFFSET, (pBoundingBox.max.z - pBoundingBox.min.z) + SelectionDefault.SELECTION_BOX_OFFSET);
        tMPivot.multiplyToRef(Matrix.Translation(tCenter.x, tCenter.y, tCenter.z), tMPivot);
        var tTransform = tMPivot.multiply(pTransform);
        for (var _i = 0, _a = this.mSelectionBoxSideGeom; _i < _a.length; _i++) {
            var tSideGeom = _a[_i];
            var tSide = tSideGeom.clone("selectionBoxSide");
            tSide.color = tColor;
            setLocalMatrix(tSide, tTransform);
            tSide.boxSide_normal = Vector3.TransformNormal(tSideGeom.boxSide_normal, tTransform);
            tSide.boxSide_center = Vector3.TransformCoordinates(tSideGeom.boxSide_center, tTransform);
            this.mSelectionBoxSides.push(tSide);
        }
    };
    SelectionDefault.prototype.updateSelectionBoxSides = function (pView) {
        for (var _i = 0, _a = this.mSelectionBoxSides; _i < _a.length; _i++) {
            var tSide = _a[_i];
            var tNormal = tSide.boxSide_normal;
            var tCenter = tSide.boxSide_center;
            //get vector pointing from interactor to camera
            var tCameraVec = this.getDirectionToCamera(tCenter, pView);
            var tDot = Vector3.Dot(tNormal, tCameraVec);
            if (tDot < 0.0) {
                tSide.isVisible = false;
            }
            else {
                tSide.isVisible = true;
            }
        }
    };
    SelectionDefault.prototype.startPointerDownRectangleSelect = function () {
        this.resetPickingStates();
        this.startRectangleSelect();
        if (this.mRectSelectTool != null) {
            this.mRectSelectTool.startSelection();
        }
    };
    SelectionDefault.prototype.resetPickingStates = function () {
        this.mPickedElement = null;
        this.resetLongTouchTimer();
        this.setInteractionMode(InteractionMode.None);
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    SelectionDefault.PICK_FRUSTUM_SIZE = 30;
    SelectionDefault.SELECTION_BOX_OFFSET = 0.025;
    SelectionDefault.LONG_TOUCH_TIMEOUT = 750;
    return SelectionDefault;
}(AbstractTool));
export { SelectionDefault };
